import { useDataFetch } from './use-data-fetch';

// Interfaces para los tipos de datos
export interface Country {
  _id: string;
  id: string;
  name: string;
  code: string;
}

export interface Province {
  _id: string;
  id: string;
  name: string;
  country_id: string;
}

export interface City {
  _id: string;
  id: string;
  name: string;
  province_id: string;
}

/**
 * Hook personalizado para obtener la lista de países
 * @returns Objeto con los países y el estado de la consulta
 */
export const useCountries = () => {
  const { getData } = useDataFetch();
  
  const query = getData(
    ['countries'],
    '/product/all/countries',
    {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 60, // 1 hora
      gcTime: 1000 * 60 * 60 * 2 // 2 horas
    }
  );

  // Extraer los países de la respuesta
  const countries = query.data?.data?.countries || [];
  
  return {
    countries,
    ...query
  };
};

/**
 * Hook personalizado para obtener las provincias por país
 * @param countryId ID del país
 * @returns Objeto con las provincias y el estado de la consulta
 */
export const useProvinces = (countryId: string | null) => {
  const { getData } = useDataFetch();
  
  const query = getData(
    ['provinces', countryId],
    countryId ? `/address/provinces/by-country?country_id=${countryId}` : '',
    {
      enabled: !!countryId,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 30, // 30 minutos
      gcTime: 1000 * 60 * 60 // 1 hora
    }
  );

  // Extraer las provincias de la respuesta
  const provinces = query.data?.data?.provinces || [];
  
  return {
    provinces,
    ...query
  };
};

/**
 * Hook personalizado para obtener las ciudades por provincia
 * @param provinceId ID de la provincia
 * @returns Objeto con las ciudades y el estado de la consulta
 */
export const useCities = (provinceId: string | null) => {
  const { getData } = useDataFetch();
  
  const query = getData(
    ['cities', provinceId],
    provinceId ? `/address/cities/by-province?province_id=${provinceId}` : '',
    {
      enabled: !!provinceId,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 30, // 30 minutos
      gcTime: 1000 * 60 * 60 // 1 hora
    }
  );

  // Extraer las ciudades de la respuesta
  const cities = query.data?.data?.cities || [];
  
  return {
    cities,
    ...query
  };
};

export interface Locality {
  id: string;
  name: string;
  municipality: string;
}

/**
 * Barrios / corregimientos con cobertura de Interrapidísimo del departamento
 * y, si se pasa, sólo los del municipio elegido.
 * @param provinceId ID de la provincia (departamento)
 * @param cityId ID de la ciudad (municipio)
 */
export const useLocalities = (provinceId: string | null, cityId?: string | null) => {
  const { getData } = useDataFetch();

  const query = getData(
    ['localities', provinceId, cityId || ''],
    provinceId
      ? `/address/localities/by-province?province_id=${provinceId}${cityId ? `&city_id=${cityId}` : ''}`
      : '',
    {
      enabled: !!provinceId,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 60, // 1 hora
      gcTime: 1000 * 60 * 60 * 2 // 2 horas
    }
  );

  const localities: Locality[] = query.data?.data?.localities || [];

  return {
    localities,
    ...query
  };
};
